import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, Users, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#DC2626', '#F97316', '#EAB308', '#3B82F6', '#16A34A'];

const Analytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-700">Loading analytics...</div>;

  if (!analytics) return <div className="text-sm text-slate-700">No analytics data available</div>;

  const getGapColor = (gapLevel) => {
    return gapLevel === 'High' ? 'text-red-600' : (gapLevel === 'Medium' ? 'text-yellow-600' : 'text-green-600');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="analytics-title">Analytics Dashboard</h1>
        <p className="text-sm text-slate-700 mt-2">Performance trends and competency gap analysis</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Total Appraisals</div>
              <div className="text-2xl font-semibold text-slate-900" data-testid="total-appraisals">{analytics.total_appraisals}</div>
            </div>
            <Users className="h-8 w-8 text-slate-400" />
          </div>
        </Card>

        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Finalized</div>
              <div className="text-2xl font-semibold text-slate-900" data-testid="finalized-count">{analytics.finalized_count}</div>
            </div>
            <Award className="h-8 w-8 text-green-500" />
          </div>
        </Card>

        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Pending Reviews</div>
              <div className="text-2xl font-semibold text-slate-900" data-testid="pending-reviews">
                {analytics.status_breakdown.pending_manager + analytics.status_breakdown.manager_review + analytics.status_breakdown.pending_second_level}
              </div>
            </div>
            <AlertCircle className="h-8 w-8 text-yellow-500" />
          </div>
        </Card>

        <Card className="p-4 border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors duration-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Completion Rate</div>
              <div className="text-2xl font-semibold text-slate-900" data-testid="completion-rate">
                {analytics.total_appraisals > 0 ? Math.round((analytics.finalized_count / analytics.total_appraisals) * 100) : 0}%
              </div>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-500" />
          </div>
        </Card>
      </div>

      {/* Performance Trends */}
      {analytics.performance_trends.length > 0 && (
        <Card className="border border-slate-200 bg-white rounded-sm p-6">
          <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-4">Performance Trends</h2>
          <p className="text-sm text-slate-700 mb-6">Average ratings across appraisal cycles</p>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.performance_trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="cycle" stroke="#64748B" style={{ fontSize: '12px' }} />
              <YAxis domain={[0, 5]} stroke="#64748B" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '4px' }}
                labelStyle={{ color: '#0F172A', fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line type="monotone" dataKey="avg_rating" stroke="#1E40AF" strokeWidth={2} name="Average Rating" dot={{ fill: '#1E40AF', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Competency Gap Analysis */}
      {analytics.competency_gaps.length > 0 && (
        <Card className="border border-slate-200 bg-white rounded-sm p-6">
          <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-4">Competency Gap Analysis</h2>
          <p className="text-sm text-slate-700 mb-6">Identify areas needing improvement (sorted by lowest ratings)</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics.competency_gaps} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" domain={[0, 5]} stroke="#64748B" style={{ fontSize: '12px' }} />
              <YAxis type="category" dataKey="competency" width={150} stroke="#64748B" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '4px' }}
                labelStyle={{ color: '#0F172A', fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="avg_rating" fill="#1E40AF" name="Average Rating" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          
          {/* Gap Details Table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm" data-testid="competency-gaps-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Competency</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Avg Rating</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Gap Level</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Total Reviews</th>
                </tr>
              </thead>
              <tbody>
                {analytics.competency_gaps.map((gap, idx) => (
                  <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200">
                    <td className="p-4 text-slate-700 font-medium">{gap.competency}</td>
                    <td className="p-4 text-slate-900 font-semibold">{gap.avg_rating.toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`font-semibold ${getGapColor(gap.gap_level)}`}>
                        {gap.gap_level}
                      </span>
                    </td>
                    <td className="p-4 text-slate-700">{gap.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Rating Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200 bg-white rounded-sm p-6">
          <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-4">Rating Distribution</h2>
          <p className="text-sm text-slate-700 mb-6">Distribution of final calibrated ratings</p>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.rating_distribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ rating, count }) => `${rating}: ${count}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {analytics.rating_distribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Manager Performance (HR Admin only) */}
        {user?.role === 'HR_ADMIN' && analytics.manager_performance.length > 0 && (
          <Card className="border border-slate-200 bg-white rounded-sm p-6">
            <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-4">Manager Performance</h2>
            <p className="text-sm text-slate-700 mb-6">Team average ratings by manager</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="manager-performance-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Team Avg</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Team Size</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.manager_performance.map((mgr, idx) => (
                    <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200">
                      <td className="p-3 text-slate-700 font-medium">{mgr.manager_name}</td>
                      <td className="p-3 text-slate-900 font-semibold">{mgr.team_avg_rating.toFixed(2)}</td>
                      <td className="p-3 text-slate-700">{mgr.team_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Status Breakdown */}
      <Card className="border border-slate-200 bg-white rounded-sm p-6">
        <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-4">Workflow Status Breakdown</h2>
        <p className="text-sm text-slate-700 mb-6">Current status of all appraisals</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Draft</div>
            <div className="text-2xl font-semibold text-slate-900">{analytics.status_breakdown.draft}</div>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 mb-2">Pending Manager</div>
            <div className="text-2xl font-semibold text-blue-900">{analytics.status_breakdown.pending_manager}</div>
          </div>
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-yellow-700 mb-2">Manager Review</div>
            <div className="text-2xl font-semibold text-yellow-900">{analytics.status_breakdown.manager_review}</div>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-700 mb-2">2nd Level</div>
            <div className="text-2xl font-semibold text-purple-900">{analytics.status_breakdown.pending_second_level}</div>
          </div>
          <div className="p-4 bg-green-50 border border-green-200 rounded-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-green-700 mb-2">Finalized</div>
            <div className="text-2xl font-semibold text-green-900">{analytics.status_breakdown.finalized}</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Analytics;
